import React, { ReactNode, useMemo } from 'react';
import MenuItem from '../../MenuItem/MenuItem';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { highlightedRowState } from '../../../../../../../atoms/layout/layout.atom';
import { roleAccessState } from '../../../../../../../atoms/auth/auth.atom';
import { hasRoleAccess } from '../../../../../../../utils/access/access';
import { HttpMethod } from '../../../../../../../domain/Role';
import {
  QueryParamsConfig,
  useLinkGeneration,
} from '../../../../../../../hooks/useLinkGeneration/useLinkGeneration';
import { ParentObjectType } from '../../../../../../List/List.helpers';

export type NavigateActionAccess = {
  url: string;
  method: HttpMethod;
};

export type NavigateActionProps = {
  to: string;
  itemId?: string;
  onSuccess?: () => void;
  baseApiUrl?: string;
  access?: NavigateActionAccess;
  includeRedirect?: boolean;
  parentObject?: ParentObjectType;
  icon?: ReactNode;
  label?: string;
  queryParamsConfig?: QueryParamsConfig[];
  valueKey?: string;
};

export const NavigateAction = ({
  to,
  itemId,
  onSuccess,
  baseApiUrl,
  access,
  includeRedirect,
  parentObject,
  icon,
  label,
  queryParamsConfig,
}: NavigateActionProps) => {
  const setHighlightedRowState = useSetRecoilState(highlightedRowState);
  const roleAccessValue = useRecoilValue(roleAccessState);
  const navigate = useNavigate();

  const link = useLinkGeneration(
    to,
    parentObject,
    includeRedirect,
    queryParamsConfig,
  );

  const handleClick = (e: any) => {
    e.stopPropagation();
    setHighlightedRowState(itemId ?? null);
    navigate(link, { replace: true });
    onSuccess?.();
  };

  const isEnabled = useMemo(() => {
    if (access) {
      return hasRoleAccess(roleAccessValue, access.method, access.url);
    }

    return hasRoleAccess(
      roleAccessValue,
      HttpMethod.PATCH,
      `${baseApiUrl}/:id`,
    );
  }, [access, baseApiUrl]);

  return (
    <MenuItem onClick={handleClick} disabled={!isEnabled}>
      {icon}
      {label}
    </MenuItem>
  );
};

export default NavigateAction;
